/**
 * DataSyncService.js
 * Service for handling offline data storage and online synchronization of tree data
 */

import { treeAPI } from './ApiService';

/**
 * Store to keep track of pending uploads that need to be synced
 */
export const getPendingUploads = () => {
  try {
    const pendingData = localStorage.getItem('pendingTreeUploads');
    return pendingData ? JSON.parse(pendingData) : [];
  } catch (error) {
    console.error('Error retrieving pending uploads:', error);
    return [];
  }
};

/**
 * Add tree data to local storage and mark for future sync if offline
 * @param {Object} treeData - The tree data to save
 * @returns {Promise} - Resolves with success status
 */
export const saveTreeData = (treeData) => {
  return new Promise((resolve, reject) => {
    try {
      // Add ID and sync status to tree data
      const dataToSave = {
        ...treeData,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        synced: false,
        syncAttempts: 0,
        lastSyncAttempt: null
      };
      
      // Save to local tree records
      const existingRecords = JSON.parse(localStorage.getItem('treeRecords') || '[]');
      existingRecords.push(dataToSave);
      localStorage.setItem('treeRecords', JSON.stringify(existingRecords));
      
      // Also add to pending uploads queue
      const pendingUploads = getPendingUploads();
      pendingUploads.push(dataToSave);
      localStorage.setItem('pendingTreeUploads', JSON.stringify(pendingUploads));
      
      // Try to sync immediately if online
      if (navigator.onLine) {
        syncData()
          .then(() => {
            resolve({ success: true, message: 'Data saved and synced successfully' });
          })
          .catch(error => {
            // Even if sync fails, the local save succeeded
            resolve({ success: true, synced: false, message: 'Data saved locally, sync failed: ' + error.message });
          });
      } else {
        resolve({ success: true, synced: false, message: 'Data saved locally (offline mode)' });
      }
    } catch (error) {
      reject({ success: false, message: 'Failed to save data: ' + error.message });
    }
  });
};

/**
 * Check if device is online
 * @returns {boolean} - True if online, false if offline
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Synchronize pending tree data with backend server
 * @returns {Promise} - Resolves when sync is complete
 */
export const syncData = () => {
  return new Promise(async (resolve, reject) => {
    if (!navigator.onLine) {
      reject(new Error('Device is offline'));
      return;
    }
    
    const pendingUploads = getPendingUploads();
    
    if (pendingUploads.length === 0) {
      resolve({ success: true, message: 'No pending uploads to sync' });
      return;
    }
    
    let syncedCount = 0;
    let failedUploads = [];
    
    try {
      // Process each pending upload
      for (const treeData of pendingUploads) {
        try {
          // Prepare data for backend (remove local-only fields)
          const { id, synced, syncAttempts, lastSyncAttempt, ...backendData } = treeData;
          
          // Upload to backend
          await treeAPI.createTree(backendData);
          
          // Mark as synced in local storage
          const treeRecords = JSON.parse(localStorage.getItem('treeRecords') || '[]');
          const recordIndex = treeRecords.findIndex(record => record.id === id);
          if (recordIndex !== -1) {
            treeRecords[recordIndex].synced = true;
            treeRecords[recordIndex].syncedAt = new Date().toISOString();
          }
          localStorage.setItem('treeRecords', JSON.stringify(treeRecords));
          
          syncedCount++;
        } catch (error) {
          console.error(`Failed to sync tree data with ID ${treeData.id}:`, error);
          
          // Update sync attempt count
          const treeRecords = JSON.parse(localStorage.getItem('treeRecords') || '[]');
          const recordIndex = treeRecords.findIndex(record => record.id === treeData.id);
          if (recordIndex !== -1) {
            treeRecords[recordIndex].syncAttempts = (treeRecords[recordIndex].syncAttempts || 0) + 1;
            treeRecords[recordIndex].lastSyncAttempt = new Date().toISOString();
          }
          localStorage.setItem('treeRecords', JSON.stringify(treeRecords));
          
          failedUploads.push({ ...treeData, error: error.message });
        }
      }
      
      // Remove successfully synced items from pending uploads
      const remainingPending = failedUploads.map(({ error, ...data }) => data);
      localStorage.setItem('pendingTreeUploads', JSON.stringify(remainingPending));
      
      if (syncedCount === pendingUploads.length) {
        resolve({ 
          success: true, 
          message: `Synchronized ${syncedCount} record(s) successfully` 
        });
      } else {
        resolve({
          success: true,
          partial: true,
          message: `Synchronized ${syncedCount}/${pendingUploads.length} records. ${failedUploads.length} failed.`,
          failed: failedUploads.length
        });
      }
    } catch (error) {
      reject(new Error('Error during sync process: ' + error.message));
    }
  });
};

/**
 * Setup event listeners to automatically sync when coming online
 */
export const setupSyncListeners = () => {
  window.addEventListener('online', () => {
    console.log('Device is online. Attempting to sync data...');
    syncData()
      .then(result => {
        console.log('Auto-sync successful:', result.message);
      })
      .catch(error => {
        console.error('Auto-sync failed:', error.message);
      });
  });
};

/**
 * Get sync status statistics
 * @returns {Object} - Stats about synced vs unsynced records
 */
export const getSyncStats = () => {
  try {
    const treeRecords = JSON.parse(localStorage.getItem('treeRecords') || '[]');
    const syncedRecords = treeRecords.filter(record => record.synced);
    const unsyncedRecords = treeRecords.filter(record => !record.synced);
    
    return {
      total: treeRecords.length,
      synced: syncedRecords.length,
      unsynced: unsyncedRecords.length,
      hasPending: unsyncedRecords.length > 0
    };
  } catch (error) {
    console.error('Error getting sync stats:', error);
    return { total: 0, synced: 0, unsynced: 0, hasPending: false };
  }
}; 